// SNIGJ - Currency Context for managing exchange rates and conversions
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const CurrencyContext = createContext(null);

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'Dollar américain', nameEn: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro', nameEn: 'Euro' },
  { code: 'CDF', symbol: 'FC', name: 'Franc congolais', nameEn: 'Congolese Franc' }
];

export const CurrencyProvider = ({ children }) => {
  const [preferredCurrency, setPreferredCurrency] = useState(
    localStorage.getItem('snigj_currency') || 'USD'
  );
  const [exchangeRates, setExchangeRates] = useState({
    USD: 1,
    EUR: 0.92,
    CDF: 2750
  });
  const [ratesLastUpdated, setRatesLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load exchange rates from backend
  const loadRates = useCallback(async () => {
    try {
      const response = await api.get('/currency-rates');
      if (response.data?.rates) {
        setExchangeRates(response.data.rates);
        setRatesLastUpdated(response.data.updated_at);
      }
    } catch (error) {
      // Silently fail - use default rates
      console.log('Using default exchange rates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRates();
  }, [loadRates]);

  // Update preferred currency
  const updatePreferredCurrency = useCallback((currency) => {
    setPreferredCurrency(currency);
    localStorage.setItem('snigj_currency', currency);
  }, []);

  // Convert amount from one currency to another
  const convertAmount = useCallback((amount, fromCurrency = 'USD', toCurrency = preferredCurrency) => {
    if (!amount || isNaN(amount)) return 0;
    if (fromCurrency === toCurrency) return amount;
    
    const fromRate = exchangeRates[fromCurrency] || 1;
    const toRate = exchangeRates[toCurrency] || 1;
    
    // Convert to USD first (base currency), then to target
    const inUSD = amount / fromRate;
    return inUSD * toRate;
  }, [exchangeRates, preferredCurrency]);

  // Format currency with appropriate symbol and decimals
  const formatCurrency = useCallback((amount, currencyCode = preferredCurrency) => {
    const currency = CURRENCIES.find(c => c.code === currencyCode);
    const symbol = currency?.symbol || '$';
    
    // CDF doesn't use decimals
    if (currencyCode === 'CDF') {
      return `${Math.round(amount).toLocaleString()} ${symbol}`;
    }
    
    return `${symbol}${amount.toFixed(2)}`;
  }, [preferredCurrency]);

  // Convert and format in one call
  const displayAmount = useCallback((amount, fromCurrency = 'USD') => {
    const converted = convertAmount(amount, fromCurrency, preferredCurrency);
    return formatCurrency(converted, preferredCurrency);
  }, [convertAmount, formatCurrency, preferredCurrency]);

  // Get currency info
  const getCurrencyInfo = useCallback((code) => {
    return CURRENCIES.find(c => c.code === code);
  }, []);

  const value = {
    preferredCurrency,
    updatePreferredCurrency,
    exchangeRates,
    ratesLastUpdated,
    loading,
    refreshRates: loadRates,
    convertAmount,
    formatCurrency,
    displayAmount,
    getCurrencyInfo,
    currencies: CURRENCIES
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
